import React from "react";
import { Box } from "@mui/joy";
import type {
  Training,
  TrainingSearchRequest,
  TrainingsPaginationRequest,
} from "../../../api/training/Training.typed";
import { TrainingStatusCode } from "../../../api/training/Training.typed";
import type { ZekayCustomer } from "../../../api/customers/Customer.typed";
import { useTraining, useCustomer } from "../../../contexts/api";
import {
  DEFAULT_PAGINATION_LIMIT,
  extractDataFromResponse,
  extractPaginationMetadata,
} from "../../../utils/paginationHelpers";
import Pagination from "../../common/Pagination";
import ScrollableListPageLayout from "../../common/ScrollableListPageLayout";
import DarkTypography from "../../common/DarkTypography";
import IconButtonCTA from "../../common/IconButtonCTA";
import { SClose } from "../../../styles/StyledMUI";
import { colorTokens } from "../../../styles/globalStyles";
import CardValidationItem from "./display/cardValidationItem";
import TrainingsEmptyState from "../common/TrainingsEmptyState";
import { useLocation, useNavigate } from "react-router-dom";
import { useTrainingsRouteIntent } from "../common/hooks/useTrainingsRouteIntent";
import { useTrainingRowHighlight } from "../common/hooks/useTrainingRowHighlight";
import ActiveAlertChip from "../../common/ActiveAlertChip";

const MIN_SEARCH_LENGTH = 2;

type NameFilter = { templateId: number; query: string };

type AlertFilter = { key: string; name: string; templateId?: number };

export default function CardValidationList() {
  const location = useLocation();
  const navigate = useNavigate();
  const routeIntent = useTrainingsRouteIntent();

  const [trainings, setTrainings] = React.useState<Training[] | undefined>([]);
  const [customersMap, setCustomersMap] = React.useState<Record<number, ZekayCustomer>>({});
  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [nameFilter, setNameFilter] = React.useState<NameFilter | null>(() => {
    const query = routeIntent.searchQuery?.trim() ?? "";
    if (routeIntent.trainingTemplateId == null || query.length < MIN_SEARCH_LENGTH) return null;

    return { templateId: routeIntent.trainingTemplateId, query };
  });
  const [alertFilter, setAlertFilter] = React.useState<AlertFilter | null>(() =>
    routeIntent.alertKey
      ? {
          key: routeIntent.alertKey,
          name: routeIntent.alertName ?? "",
          templateId: routeIntent.trainingTemplateId,
        }
      : null,
  );

  const [currentPage, setCurrentPage] = React.useState(1);
  const [totalPages, setTotalPages] = React.useState(0);
  const [totalItems, setTotalItems] = React.useState(0);
  const [itemsPerPage, setItemsPerPage] = React.useState(DEFAULT_PAGINATION_LIMIT);

  const { getDoneTrainings, searchTrainings } = useTraining();
  const { getCustomersBatch } = useCustomer();

  React.useEffect(() => {
    const state = location.state as typeof routeIntent | null;
    if (!state?.alertKey) return;
    setAlertFilter({
      key: state.alertKey,
      name: state.alertName ?? "",
      templateId: state.trainingTemplateId,
    });
    setNameFilter(null);
    setCurrentPage(1);
    navigate(location.pathname, {
      replace: true,
      state: { select: state.select || "Cartes" },
    });
  }, [location.state, location.pathname, navigate]);

  const { highlightRowRef, matchesTarget, flashActive } = useTrainingRowHighlight({
    trainings,
    highlightTrainingId: routeIntent.targetTrainingId,
    highlightCustomerId: routeIntent.targetCustomerId,
  });

  const loadTrainings = React.useCallback(
    async (page = 1, limit = itemsPerPage): Promise<Training[]> => {
      setIsLoading(true);
      try {
        let result;
        if (nameFilter) {
          const searchRequest: TrainingSearchRequest = {
            template_id: nameFilter.templateId,
            status_code: TrainingStatusCode.Done,
            query: nameFilter.query,
            page,
            limit,
            alert: alertFilter?.key,
          };
          result = await searchTrainings(searchRequest);
        } else {
          const paginationParams: TrainingsPaginationRequest = {
            page,
            limit,
            template_id: alertFilter?.templateId,
            alert: alertFilter?.key,
          };
          result = await getDoneTrainings(paginationParams);
        }

        const trainingsData = extractDataFromResponse(result) as Training[];
        const ids = (trainingsData ?? [])
          .map((t) => t.CustomerID)
          .filter((id): id is number => id != null);
        const customerIds = Array.from(new Set(ids));
        if (customerIds.length > 0) {
          const batch = await getCustomersBatch(customerIds);
          if (Array.isArray(batch)) {
            const map: Record<number, ZekayCustomer> = {};
            batch.forEach((c) => {
              if (c.Id != null) map[c.Id] = c;
            });
            setCustomersMap(map);
          }
        } else {
          setCustomersMap({});
        }
        setTrainings(trainingsData);
        const metadata = extractPaginationMetadata(result);
        if (metadata) {
          setTotalItems(metadata.total);
          setTotalPages(metadata.total_pages);
          setCurrentPage(page);
        } else {
          setTotalItems(trainingsData.length);
          setTotalPages(1);
          setCurrentPage(1);
        }

        return trainingsData;
      } catch {
        setTrainings([]);
        setTotalItems(0);
        setTotalPages(0);

        return [];
      } finally {
        setIsLoading(false);
      }
    },
    [getDoneTrainings, searchTrainings, getCustomersBatch, itemsPerPage, nameFilter, alertFilter],
  );

  React.useEffect(() => {
    loadTrainings(1, itemsPerPage);
  }, [itemsPerPage, loadTrainings]);

  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= totalPages) {
      loadTrainings(newPage, itemsPerPage);
    }
  };

  const handleItemsPerPageChange = (newLimit: number) => {
    setItemsPerPage(newLimit);
    setCurrentPage(1);
  };

  const refreshTrainings = React.useCallback(async () => {
    return loadTrainings(currentPage, itemsPerPage);
  }, [currentPage, itemsPerPage, loadTrainings]);

  const updateTrainingLocally = React.useCallback((updated: Training) => {
    setTrainings((prev) => prev?.map((t) => (t.ID === updated.ID ? updated : t)));
  }, []);

  return (
    <ScrollableListPageLayout
      isLoading={isLoading}
      pagination={
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          totalItems={totalItems}
          itemsPerPage={itemsPerPage}
          onPageChange={handlePageChange}
          onItemsPerPageChange={handleItemsPerPageChange}
          loading={isLoading}
        />
      }
    >
      {(nameFilter || alertFilter) && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 1,
            mb: 1,
            px: 1.5,
            py: 0.75,
            borderRadius: "10px",
            border: `1px solid ${colorTokens.border.default}`,
            bgcolor: colorTokens.bg.surface,
          }}
        >
          {alertFilter ? (
            <ActiveAlertChip alertName={alertFilter.name} onClear={() => setAlertFilter(null)} />
          ) : null}
          {nameFilter && (
            <>
              <DarkTypography level="body-sm" colorVariant="primary">
                Filtré : {nameFilter.query}
              </DarkTypography>
              <IconButtonCTA
                ctaVariant="accent"
                aria-label="Effacer le filtre"
                size="sm"
                onClick={() => setNameFilter(null)}
              >
                <SClose />
              </IconButtonCTA>
            </>
          )}
        </Box>
      )}

      <Box sx={{ display: "flex", flexDirection: "column", gap: 0, width: "100%" }}>
        {trainings && trainings.length > 0
          ? trainings.map((t, index) => {
              const isTarget = matchesTarget(t);

              return (
                <Box
                  key={t.ID ?? t.CustomerID ?? index}
                  ref={isTarget ? highlightRowRef : undefined}
                >
                  <CardValidationItem
                    training={t}
                    initialCustomer={t.CustomerID != null ? customersMap[t.CustomerID] : undefined}
                    setTrainings={setTrainings}
                    refreshTrainings={refreshTrainings}
                    updateTrainingLocally={updateTrainingLocally}
                    highlighted={isTarget && flashActive}
                  />
                </Box>
              );
            })
          : trainings?.length === 0 &&
            !isLoading && (
              <Box sx={{ py: 4 }}>
                <TrainingsEmptyState
                  message={nameFilter || alertFilter ? "Aucun résultat" : "Aucune carte à valider"}
                  dense
                />
              </Box>
            )}
      </Box>
    </ScrollableListPageLayout>
  );
}
